import React, {
	useEffect,
	useCallback,
	useState,
	useMemo,
	useRef,
} from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { IdleTimerProvider } from 'react-idle-timer';
import { Navigate, useLocation, useNavigate } from 'react-router-dom';
import {
	setAccessToken,
	clearAccessToken,
	clearRememberMe,
} from '../redux/auth/AuthSlice';
import { authApi } from '../api/';
import SessionInactivityPopup from '../components/onboarding/SessionInactivityPopup';
import { supabase } from '../supabaseClient';
import throttle from 'lodash/throttle';
import { getBuildings } from '../redux/buildings/BuildingsSlice';
import { getUserDetails } from '../redux/user/UserSlice';
import { store } from '../Store';

const WithAuth = ({ children }) => {
	const dispatch = useDispatch();
	const accessToken = useSelector((state) => state.auth.accessToken);
	const rememberMe = useSelector((state) => state.auth.rememberMe);
	const [hasBeenIdle, setHasBeenIdle] = useState(false);
	const [showSessionPopup, setShowSessionPopup] = useState(false);
	const [isLoading, setIsLoading] = useState(true); // New state to handle loading
	const location = useLocation();
	const timerRef = useRef(null);
	const refreshLock = useRef(false);
	const navigate = useNavigate();

	const pathsToExclude = [
		'/login',
		'/signup',
		'/forgotPassword',
		'/resetPassword',
		'/signupConfirm',
	];

	// Verify session on mount
	useEffect(() => {
		const verifyInitialSession = async () => {
			const { data } = await supabase.auth.getSession();
			if (!data.session) {
				dispatch(clearAccessToken());
				setShowSessionPopup(true);
			} else {
				dispatch(setAccessToken(data.session.access_token));
			}
			setIsLoading(false); // Done processing
		};

		verifyInitialSession();
	}, [dispatch]);

	const refreshTokenIfNeeded = useCallback(async () => {
		if (refreshLock.current || showSessionPopup) return null;
		refreshLock.current = true;

		try {
			const { data, error } = await supabase.auth.getSession();
			if (error || !data.session) {
				setShowSessionPopup(true);
				dispatch(clearAccessToken());
				return null;
			}

			const expirationTime = data.session.expires_at * 1000;
			const currentTime = new Date().getTime();
			const timeLeft = expirationTime - currentTime;

			if (timeLeft < 300 * 1000) {
				const { data: newSessionData, error: newSessionError } =
					await supabase.auth.refreshSession();
				if (newSessionError || !newSessionData.session) {
					setShowSessionPopup(true);
					dispatch(clearAccessToken());
					return null;
				}
				dispatch(setAccessToken(newSessionData.session.access_token));
			}

			return Math.max(timeLeft - 300 * 1000, 10 * 1000);
		} catch (err) {
			setShowSessionPopup(true);
			dispatch(clearAccessToken());
			return null;
		} finally {
			refreshLock.current = false;
		}
	}, [dispatch, showSessionPopup]);

	const scheduleTokenCheck = useCallback(async () => {
		if (timerRef.current) {
			clearTimeout(timerRef.current);
		}
		const nextCheckDelay = await refreshTokenIfNeeded();
		if (nextCheckDelay != null) {
			timerRef.current = setTimeout(scheduleTokenCheck, nextCheckDelay);
		} else {
			timerRef.current = null;
		}
	}, [refreshTokenIfNeeded]);

	const handleOnIdle = () => {
		if (!pathsToExclude.includes(location.pathname)) {
			clearTimeout(timerRef.current);
			dispatch(clearAccessToken());
			dispatch(clearRememberMe());
			setHasBeenIdle(true);
		}
	};

	const handleLoginButtonClick = () => {
		setHasBeenIdle(false);
		setShowSessionPopup(false);
		authApi.signOut();
		dispatch(clearAccessToken());
	};

	const timeout = rememberMe ? 1000 * 60 * 60 * 24 : 1000 * 60 * 30;

	const handleUserActivity = useMemo(() => {
		return throttle(() => {
			if (!showSessionPopup) {
				refreshTokenIfNeeded();
			}
		}, 10 * 1000);
	}, [refreshTokenIfNeeded, showSessionPopup]);

	useEffect(() => {
		if (!showSessionPopup) {
			window.addEventListener('mousemove', handleUserActivity);
			window.addEventListener('keydown', handleUserActivity);
			window.addEventListener('click', handleUserActivity);
			scheduleTokenCheck();
		}

		return () => {
			window.removeEventListener('mousemove', handleUserActivity);
			window.removeEventListener('keydown', handleUserActivity);
			window.removeEventListener('click', handleUserActivity);
			if (timerRef.current) {
				clearTimeout(timerRef.current);
				timerRef.current = null;
			}
		};
	}, [scheduleTokenCheck, handleUserActivity, showSessionPopup]);

	useEffect(() => {
		if (showSessionPopup && timerRef.current) {
			clearTimeout(timerRef.current);
			timerRef.current = null;
		}
	}, [showSessionPopup]);

	// Handle Magic Link Authentication
	useEffect(() => {
		let url = decodeURIComponent(window.location.href.split('/#')[1]);
		let urlParams = new URLSearchParams(url);
		let type = urlParams.get('type') || '';

		if (type === 'recovery' || type === 'signup' || type === 'invite') {
			window.location.href = url;
		} else if (type === 'magiclink') {
			(async () => {
				setIsLoading(true); // Show loading while processing magic link

				const { data, error } = await supabase.auth.getSession();
				if (data?.session) {
					// Dispatch setAccessToken (assumed to be a synchronous action)
					dispatch(setAccessToken(data.session.access_token));

					// Wait for state update by accessing the updated state
					await new Promise((resolve) => setTimeout(resolve, 0)); // Ensures next state is available

					// Get the latest state
					const updatedState = store.getState(); // Ensure you have access to store
					// Get the user ID
					const userId = updatedState.auth?.user?.id || data.session.user.id;
					if (userId) {
						await dispatch(getUserDetails({ user_id: userId }))
							.unwrap()
							.then(async (res) => {
								dispatch(getBuildings(res)).then(() => {
									navigate('/');
								});
							});
					}
				}

				setIsLoading(false); // Done processing magic link
			})();
		} else {
			setIsLoading(false);
		}
	}, [dispatch]);

	// Prevent premature navigation while loading
	if (isLoading) {
		return <div>Loading...</div>;
	}

	return accessToken ? (
		<>
			<SessionInactivityPopup
				isOpen={hasBeenIdle || showSessionPopup}
				onLoginClick={handleLoginButtonClick}
			/>
			<IdleTimerProvider timeout={timeout} onIdle={handleOnIdle} debounce={250}>
				{children}
			</IdleTimerProvider>
		</>
	) : (
		<Navigate to="/login" />
	);
};

export default WithAuth;
